<template>
  <DragProvider :disabled @drop="handleDrop">
    <template #default="{ isDraggingInPage, isDraggingInArea }">
      <div
        class="absolute inset-0"
        :class="{
          'pointer-events-none': !isDraggingInPage,
        }"
      >
        <Overlay v-if="isDraggingInPage" :is-opaque="isDraggingInArea" />
      </div>
    </template>
  </DragProvider>

  <slot :add-attachments />

  <input
    ref="attachments"
    :accept="ACCEPTED_FILE_EXTENSIONS"
    :aria-label="i18n(translations.addAttachment)"
    class="hidden"
    multiple
    name="attachments"
    type="file"
    @change="
      (event) =>
        handleAttachmentsUpload((event.target as HTMLInputElement).files)
    "
  />
  <div
    v-if="modelValue.length"
    class="my-16 grid grid-cols-1 gap-4 lg:grid-cols-3"
  >
    <RevDocument
      v-for="attachment in modelValue"
      :key="getAttachmentIdentifier(attachment.file.name, attachment.file.size)"
      class="my-4 md:mr-6"
      deletable
      deleteButtonAriaLabel="Delete"
      :errorMessage="attachment.errorMessage"
      :name="attachment.file.name"
      :status="attachment.status"
      @delete="removeAttachment(attachment)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { humanReadableSize } from '@backmarket/utils/math/humanReadableSize'
import { RevDocument } from '@ds/components/Document'
import { DocumentStatusEnum } from '@ds/components/Document/Document.constant'

import { useOrderlinePublicIdFromRoute } from '~/scopes/customer-care/pages/request/composables/useOrderlinePublicIdFromRoute'

import {
  ACCEPTED_FILE_EXTENSIONS,
  MAX_ATTACHMENTS_COUNT,
  MAX_ATTACHMENTS_SIZE,
} from './AttachmentsUpload.constants'
import translations from './AttachmentsUpload.translations'
import DragProvider from './DragAndDrop/DragProvider.vue'
import Overlay from './Overlay.vue'
import { useAttachmentUpload } from './composables/useAttachmentUpload'
import type { AttachmentUpload } from './types'

const { modelValue, disabled = false } = defineProps<{
  modelValue: AttachmentUpload[]
  disabled?: boolean
}>()
const emit = defineEmits(['update:modelValue'])

function getAttachmentIdentifier(name: string, size: number) {
  return `${name}-${size}`
}

const i18n = useI18n()
const { openErrorToast } = useTheToast()
const orderlinePublicId = useOrderlinePublicIdFromRoute()

const { uploadAttachments } = useAttachmentUpload({
  orderlinePublicId: orderlinePublicId.value,
  onAttachmentUpdate: (attachments) => emit('update:modelValue', attachments),
})

const attachments = ref<HTMLInputElement | null>(null)

const attachmentsSize = computed(() => {
  return modelValue.reduce(
    (totalSize, attachment) => totalSize + attachment.file.size,
    0,
  )
})

async function resetInput() {
  if (attachments.value) {
    attachments.value.value = ''
  }
}

async function updateValue(value: AttachmentUpload[]) {
  emit('update:modelValue', value)
  // Because file input keep in its own state the path of the first selected file,
  // we should reset it in order to be able to add or remove files
  resetInput()
}

async function removeAttachment(attachmentToRemove: AttachmentUpload) {
  const updatedAttachments = modelValue.filter(
    (attachment) =>
      getAttachmentIdentifier(attachment.file.name, attachment.file.size) !==
      getAttachmentIdentifier(
        attachmentToRemove.file.name,
        attachmentToRemove.file.size,
      ),
  )

  updateValue(updatedAttachments)
}

const abortController = ref<AbortController>(new AbortController())

async function handleAttachmentsUpload(files: FileList | null) {
  if (!files) {
    return
  }

  const availableSlots = MAX_ATTACHMENTS_COUNT - modelValue.length
  const filesToProcess = Array.from(files).slice(0, availableSlots)
  const hasExceededFileLimit = files.length > availableSlots

  if (hasExceededFileLimit) {
    openErrorToast({
      content: i18n(translations.tooManyAttachments, {
        maxCount: MAX_ATTACHMENTS_COUNT,
      }),
    })
  }

  if (filesToProcess.length === 0) {
    return
  }

  const { validFiles, hasRejectedFiles } = filesToProcess.reduce(
    (acc, file) => {
      if (acc.cumulativeSize + file.size <= MAX_ATTACHMENTS_SIZE) {
        return {
          ...acc,
          validFiles: [...acc.validFiles, file],
          cumulativeSize: acc.cumulativeSize + file.size,
        }
      }

      return { ...acc, hasRejectedFiles: true }
    },
    {
      validFiles: [] as File[],
      cumulativeSize: attachmentsSize.value,
      hasRejectedFiles: false,
    },
  )

  if (hasRejectedFiles) {
    openErrorToast({
      content: i18n(translations.tooBigAttachments, {
        maxSize: humanReadableSize(
          MAX_ATTACHMENTS_SIZE,
          i18n(translations.unit),
          1024,
        ),
      }),
    })
  }

  const attachmentsToAdd = validFiles.map((file) => ({
    file,
    status: DocumentStatusEnum.Loading,
    handle: '',
    filePath: '',
  }))

  uploadAttachments(attachmentsToAdd, modelValue, abortController.value.signal)
}

function handleDrop({ dataTransfer }: { dataTransfer: DataTransfer }) {
  if (disabled) {
    return
  }
  handleAttachmentsUpload(dataTransfer?.files)
}

function addAttachments() {
  if (attachments.value) {
    attachments.value.click()
  }
}

onBeforeUnmount(() => {
  abortController.value.abort()
})
</script>
